/**
 * `useLlmStatus` — read-only LLM model snapshot for the current org.
 *
 * Slice 3.D of `ai-docs/llm-default-model-mandatory-plan.md` (2026-05-25):
 * the data source switched from the gateway's `rpc.runtimeLlmStatus()`
 * (per-container) to lexserver's `GET /api/org/:orgId/runtime-config/llm-status`
 * (per-org). One value across browser tabs / runtimes regardless of which
 * container instance answers.
 *
 * Cache strategy (replaces the previous gateway-reconnect-only invalidation):
 *  - module-level cache keyed by orgId
 *  - `staleAfter: 5 * 60_000` (5 minutes) — operator org-admin changes
 *    appear without a hard reload
 *  - `window:"focus"` listener — refetch when the tab regains focus
 *  - ADAPTIVE while DEGRADED (2026-07-13, llm-provider-failure-plan §8):
 *    when the last snapshot shows a non-primary tier or an unhealthy
 *    provider, staleness drops to 30s and a background interval re-polls —
 *    the emergency pill/banner clears within ~30s of the org's ascent
 *    instead of lingering up to 5 minutes. Healthy snapshots keep the
 *    5-minute cadence (the interval's ticks short-circuit on fresh cache).
 *
 * Read-only for ALL users (display-only, informational). Editing is
 * org-admin-only via the @lexiun/org-admin RuntimeConfigPage (mounted at
 * /org/config in LexIDE).
 *
 * Fail-soft: a fetch failure leaves `status: null` and consumers
 * (ModelBadge / Settings AI-Engine) render an empty/unavailable state.
 */

import { useEffect, useState } from "react";
import {
  fetchOrgLlmStatus,
  type OrgActiveModel,
  type OrgLlmStatus,
} from "@/lib/lexserver-client";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";

const STALE_AFTER_MS = 5 * 60_000;
/** Staleness while the org is on a degraded tier — the pill must clear fast. */
const DEGRADED_STALE_AFTER_MS = 30_000;

interface CacheEntry {
  status: OrgLlmStatus;
  fetchedAt: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<OrgLlmStatus>>();

/** Drop the cached snapshots (sign-out / tests / forced refresh). */
export function clearLlmStatusCache(): void {
  cache.clear();
  inFlight.clear();
}

/** Any signal that the org is not on full primary service. */
function isDegraded(status: OrgLlmStatus): boolean {
  if (status.effectiveTiers?.some((t) => t.tier !== "primary" && t.tier !== "platform")) {
    return true;
  }
  const health = status.providerHealth;
  return health != null && Object.values(health).some((h) => h.status !== "ok");
}

function isStale(entry: CacheEntry): boolean {
  const ttl = isDegraded(entry.status) ? DEGRADED_STALE_AFTER_MS : STALE_AFTER_MS;
  return Date.now() - entry.fetchedAt > ttl;
}

export interface UseLlmStatusResult {
  /** Authoritative chat-default for the org. `null` while loading or
   *  when the fetch failed (consumer renders empty/unavailable state). */
  status: OrgLlmStatus | null;
  /** Convenience: the `activeModel` slice (most consumers want just this). */
  activeModel: OrgActiveModel | null;
  loading: boolean;
}

export function useLlmStatus(): UseLlmStatusResult {
  const { identity } = useAuthIdentity();
  const orgId = identity?.orgId;
  const cached = orgId ? cache.get(orgId) ?? null : null;
  const [status, setStatus] = useState<OrgLlmStatus | null>(cached?.status ?? null);
  const [loading, setLoading] = useState(cached == null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;

    const load = async (force: boolean): Promise<void> => {
      const existing = cache.get(orgId);
      if (!force && existing && !isStale(existing)) {
        setStatus(existing.status);
        setLoading(false);
        return;
      }
      setLoading(true);
      const pending =
        inFlight.get(orgId) ??
        (() => {
          const p = fetchOrgLlmStatus(orgId);
          inFlight.set(orgId, p);
          return p;
        })();
      try {
        const fresh = await pending;
        cache.set(orgId, { status: fresh, fetchedAt: Date.now() });
        if (!cancelled) {
          setStatus(fresh);
          setLoading(false);
        }
      } catch (err) {
        // Fail-soft: never block chat / never surface an error here.
        // Log at debug-level so dev-tools devs can see why the badge
        // didn't render (e.g., new endpoint not yet deployed).
        // eslint-disable-next-line no-console
        console.debug("[useLlmStatus] fetch failed:", (err as Error).message);
        if (!cancelled) {
          setStatus(null);
          setLoading(false);
        }
      } finally {
        if (inFlight.get(orgId) === pending) inFlight.delete(orgId);
      }
    };

    // Initial load — serves cache if fresh, otherwise refetches.
    void load(false);

    // Refetch on tab focus (operator org-admin changes appear without a
    // hard reload). Only fires when the cached entry is stale OR absent —
    // `load(false)` short-circuits on fresh cache so this is cheap.
    const onFocus = (): void => {
      void load(false);
    };
    window.addEventListener("focus", onFocus);

    // Adaptive re-poll: ticks short-circuit on fresh cache, so this only
    // actually refetches once the entry crosses its TTL — every 30s while
    // the org is degraded (fast pill-clear on ascent), every 5 min when
    // healthy. Same-reference setState on a short-circuit is a React no-op.
    const interval = window.setInterval(() => {
      void load(false);
    }, DEGRADED_STALE_AFTER_MS);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
    };
  }, [orgId]);

  return {
    status,
    activeModel: status?.activeModel ?? null,
    loading,
  };
}
